import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { networkService } from "../../config/axios";
import LoadingScreen from "../../Elements/LoadingScreen/LoadingScreen";
import "./RriPage.css";

const TIER_LABEL = {
    hot: "HOT",
    warm: "WARM",
    cold: "COLD",
    dq: "DQ",
};

const RING_RADIUS = 52;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const formatDuration = (totalSeconds) => {
    if (!totalSeconds) return "0s";
    const total = Math.round(totalSeconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const uniquePagesViewed = (sessions) => {
    const pages = new Set();
    (sessions || []).forEach((s) => (s.pages || []).forEach((p) => pages.add(p.page)));
    return pages.size;
};

const totalDwellSeconds = (sessions) => (sessions || []).reduce((sum, s) => sum + (s.total_seconds || 0), 0);

const MEDDICC_LABELS = {
    metrics: "Metrics",
    economic_buyer: "Econ buyer",
    criteria: "Criteria",
    process: "Process",
    pain: "Pain",
    champion: "Champion",
    competition: "Competition",
};

const RriPage = () => {
    const { token, receiverId } = useParams();

    const [report, setReport] = useState(null);
    const [receiver, setReceiver] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        if (!token || !receiverId) {
            setLoading(false);
            return undefined;
        }

        mountedRef.current = true;
        setLoading(true);
        setError(null);

        networkService(`/rri_reports/for_recipient.json?doc_token=${token}&recipient_id=${receiverId}`, "GET")
            .then((response) => {
                if (!mountedRef.current) return;
                setReport(response?.data?.rri_report ?? null);
                setReceiver(response?.data?.receiver ?? null);
                setContext(response?.data?.context ?? null);
            })
            .catch((err) => {
                if (!mountedRef.current) return;
                setError(err?.response?.data?.error || "Couldn't load the RRI scorecard. Please try again later.");
            })
            .finally(() => {
                if (mountedRef.current) setLoading(false);
            });

        return () => { mountedRef.current = false; };
    }, [token, receiverId]);

    if (loading) return <LoadingScreen />;

    if (!token || !receiverId) {
        return <div className="rriPage__empty">Missing document or recipient.</div>;
    }

    if (error) {
        return <div className="rriPage__empty">{error}</div>;
    }

    const narrative = report?.narrative;
    const readiness = narrative?.readiness || context?.readiness;
    const tier = readiness?.tier;
    const score = readiness?.score;
    const dimensions = narrative?.dimensions || [];
    const ledger = narrative?.ledger || [];
    const behavioral = narrative?.behavioral_highlights || [];
    const meddicc = narrative?.meddicc || {};
    const action = narrative?.action;
    const objectionFlags = narrative?.objection_flags || [];

    const ringOffset = score !== null && score !== undefined
        ? RING_CIRCUMFERENCE * (1 - Math.max(0, Math.min(100, score)) / 100)
        : RING_CIRCUMFERENCE;

    const pagesViewed = uniquePagesViewed(context?.sessions);
    const totalPages = context?.meta?.kdf?.total_pages;
    const sessionCount = context?.sessions?.length || 0;
    const dwellTotal = totalDwellSeconds(context?.sessions);
    const answeredCount = readiness?.answered_count ?? context?.dimension_inputs?.length ?? 0;

    return (
        <div className="rriPage">
            <div className="rriPage__bar">
                <div className="rriPage__lhs">
                    <span className="rriPage__logo">KINETIZINE</span>
                    <span className="rriPage__pill">RRI &middot; RECIPIENT RESPONSE INTELLIGENCE</span>
                </div>
                <div className="rriPage__rhs">
                    KDF: <b>{context?.meta?.kdf?.title || "—"}</b><br />
                    {sessionCount > 0 && <>Total time {formatDuration(dwellTotal)} &nbsp;|&nbsp; </>}
                    Signals captured {answeredCount}
                    {totalPages ? <>&nbsp;|&nbsp; Pages {pagesViewed} / {totalPages}</> : null}
                </div>
            </div>

            <div className="rriPage__reveal">
                <div className="rriPage__was">
                    <div className="rriPage__k">What marketing handed you</div>
                    <div className="rriPage__v">
                        <b>{receiver?.name || receiver?.email || "Unknown recipient"}</b>
                        {receiver?.name && receiver?.email && <> &middot; {receiver.email}</>}
                    </div>
                </div>
                <div className="rriPage__arrow">&rarr;</div>
                <div className="rriPage__now">
                    <div className="rriPage__k">What their choices handed you</div>
                    <div className="rriPage__v">
                        {narrative?.reveal_headline || (report ? "No summary available." : "No RRI analysis yet — it generates once this recipient answers at least one question.")}
                    </div>
                </div>
            </div>

            {!report && (
                <div className="rriPage__emptyInline">
                    No RRI scorecard yet for {receiver?.name || receiver?.email || "this recipient"} — it generates once they visit and answer at least one Navigator question.
                </div>
            )}

            {report && (
                <div className="rriPage__grid">
                    <div className="rriPage__col">

                        <div className="rriPage__card">
                            <div className="rriPage__scorecard">
                                <div className="rriPage__ringWrap">
                                    <svg width="132" height="132" viewBox="0 0 132 132">
                                        <circle cx="66" cy="66" r={RING_RADIUS} fill="none" stroke="#EDF2EF" strokeWidth="13" />
                                        <circle
                                            cx="66" cy="66" r={RING_RADIUS} fill="none" stroke="#0C9A6E" strokeWidth="13"
                                            strokeLinecap="round" transform="rotate(-90 66 66)"
                                            strokeDasharray={RING_CIRCUMFERENCE}
                                            strokeDashoffset={ringOffset}
                                        />
                                    </svg>
                                    <div className="rriPage__ringNum">
                                        <span className="n">{score ?? "—"}</span>
                                        <span className="l">Readiness</span>
                                    </div>
                                </div>
                                <div className="rriPage__scoreRight">
                                    <div className="rriPage__tierrow">
                                        <span className={`rriPage__tier rriPage__tier--${tier || "cold"}`}>{TIER_LABEL[tier] || "—"}</span>
                                        {readiness?.band_low !== undefined && readiness?.band_low !== null && (
                                            <span className="rriPage__bri">Band &nbsp;<b>{readiness.band_low}–{readiness.band_high}</b></span>
                                        )}
                                        {readiness?.trajectory && <span className="rriPage__trajectory">{readiness.trajectory}</span>}
                                    </div>
                                    <div className="rriPage__bars">
                                        {dimensions.length === 0 && <div className="rriPage__noDimensions">No answered questions to break down yet.</div>}
                                        {dimensions.map((d) => (
                                            <div className="rriPage__barline" key={d.label}>
                                                <span className="bl">{d.label}</span>
                                                <span className="track"><span className="fill" style={{ width: `${Math.max(0, Math.min(100, d.score))}%` }} /></span>
                                                <span className="bv">{d.score}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="rriPage__formulaNote">
                                        Readiness is one deterministic score (Navigator engine). Bars above show each answered question&apos;s relative strength, grouped and named for this document.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="rriPage__card">
                            <h3>Choices &rarr; Meaning</h3>
                            <div className="rriPage__sub">What they picked, and what it means.</div>
                            <div className="rriPage__ledger">
                                {ledger.length === 0 && <div className="rriPage__noDimensions">No questions answered yet.</div>}
                                {ledger.map((entry, idx) => (
                                    <div className="rriPage__qrow" key={idx}>
                                        <div className="rriPage__qnum">{String(idx + 1).padStart(2, "0")}</div>
                                        <div className="rriPage__qbody">
                                            <div className="rriPage__qtext">{entry.question_title}</div>
                                            <div className="rriPage__chose">
                                                <span className="lab">Chose</span>
                                                <span className="opt">{entry.question_answer}</span>
                                            </div>
                                            {entry.signal_tags?.length > 0 && (
                                                <div className="rriPage__derive">
                                                    <span className="to">&rarr;</span>
                                                    {entry.signal_tags.map((tag, tIdx) => (
                                                        <span className="rriPage__sig" key={tIdx}>{tag}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rriPage__card">
                            <h3>What they did, not just what they said</h3>
                            <div className="rriPage__sub">The silent layer.</div>
                            <div className="rriPage__beh">
                                {behavioral.length === 0 && <div className="rriPage__noDimensions">No standout behavioral signal yet.</div>}
                                {behavioral.map((b, idx) => (
                                    <div className="rriPage__behrow" key={idx}>
                                        <span className="m">{b.observation}</span>
                                        <span className="d">{b.coaching_note}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="rriPage__col">
                        {action && (
                            <div className="rriPage__card rriPage__action">
                                <h3>Your next call</h3>
                                <div className="rriPage__sub">Who to call, why, and what to say.</div>

                                <div className="rriPage__act"><div className="lab">Who</div><div className="txt">{action.who}</div></div>
                                <div className="rriPage__act"><div className="lab">Why</div><div className="txt">{action.why}</div></div>
                                {action.open_with && <div className="rriPage__act"><div className="lab">Open with</div><div className="txt">{action.open_with}</div></div>}
                                {action.do_not && <div className="rriPage__act rriPage__act--dont"><div className="lab">Do not</div><div className="txt">{action.do_not}</div></div>}
                                {action.arm_them && <div className="rriPage__act"><div className="lab">Arm them</div><div className="txt">{action.arm_them}</div></div>}
                                <div className="rriPage__act" style={{ marginBottom: 0 }}><div className="lab">Next step</div><div className="txt">{action.next_step}</div></div>

                                {objectionFlags.length > 0 && (
                                    <>
                                        <div className="rriPage__divider" />
                                        <div className="rriPage__flagsLabel">Objection flags</div>
                                        <div className="rriPage__flags">
                                            {objectionFlags.map((f, idx) => (
                                                <div className={`rriPage__flag ${f.severity === "high" ? "rriPage__flag--high" : ""}`} key={idx}>
                                                    <span className="dot" /><span>{f.text}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        <div className="rriPage__card">
                            <h3>MEDDICC, pre call</h3>
                            <div className="rriPage__sub">Filled in from what they told you and did.</div>
                            <div className="rriPage__meddicc">
                                {Object.keys(MEDDICC_LABELS).map((key) => (
                                    meddicc[key] ? (
                                        <React.Fragment key={key}>
                                            <span className="me">{MEDDICC_LABELS[key]}</span>
                                            <span className="mv">{meddicc[key]}</span>
                                        </React.Fragment>
                                    ) : null
                                ))}
                                {Object.keys(MEDDICC_LABELS).every((key) => !meddicc[key]) && (
                                    <span className="rriPage__noDimensions">Not enough signal yet to fill this in.</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {report && (
                <div className="rriPage__foot">
                    <div className="rriPage__footQuote">
                        {narrative?.confidence_note}
                        {narrative?.watch_out && <><br /><b>Watch out:</b> {narrative.watch_out}</>}
                    </div>
                    <div className="rriPage__footMeta">
                        Generated {report.created_at ? new Date(report.created_at).toLocaleString() : "—"}
                        {report.source === "fallback" && <><br />generated without AI narrative</>}
                        {report.source === "guard" && <><br />blocked before analysis — flagged as test/internal traffic</>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RriPage;
